'use client'

import { paths } from '@/config/paths'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { MarketsOverviewSection } from '@/features/markets-overview/MarketsOverview'
import { FeaturedOpportunities } from '@/features/unified-earn/components/FeaturedOpportunities'
import { UnifiedEarnTableSection } from '@/features/unified-earn/components/UnifiedEarnTableSection'
import {
  UnifiedEarnOpportunitiesProvider,
  useSharedUnifiedEarnOpportunities,
} from '@/features/unified-earn/logic/useUnifiedEarnOpportunities'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { PageHeroStats } from '@/ui/molecules/page-hero/PageHeroStats'
import { PillNav } from '@/ui/molecules/page-hero/PillNav'
import { Boxes, Rows3, Shield } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'

function OverviewStatsContent() {
  const [hasMounted, setHasMounted] = useState(false)
  const { aggregateMarketStats, isLoading } = useSharedUnifiedEarnOpportunities()

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const showLoadingState = !hasMounted || isLoading
  const formatUsd = (value: number) => USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(value), { compact: true })
  const stats = [
    { label: 'Supplied', value: showLoadingState ? '' : formatUsd(aggregateMarketStats.totalSuppliedUSD.toNumber()) },
    { label: 'Borrowed', value: showLoadingState ? '' : formatUsd(aggregateMarketStats.totalBorrowedUSD.toNumber()) },
    {
      label: 'Available',
      value: showLoadingState ? '' : formatUsd(aggregateMarketStats.totalAvailableUSD.toNumber()),
    },
  ]

  return <PageHeroStats stats={stats} loading={showLoadingState} />
}

type MarketsViewMode = 'markets' | 'assets'

interface MarketsHubPageProps {
  defaultView?: MarketsViewMode
  basePath?: string
  assetsPath?: string
  vaultsPath?: string
  title?: string
  subtitle?: string
}

export function MarketsHubPage({
  defaultView,
  basePath = paths.earn,
  assetsPath = paths.earnAssets,
  vaultsPath = paths.earnVaults,
  title = 'Markets',
  subtitle = 'Browse managed strategies and lending markets. Select a product to view available assets, current rates, and risk parameters.',
}: MarketsHubPageProps) {
  const viewMode: MarketsViewMode = defaultView ?? 'markets'

  return (
    <UnifiedEarnOpportunitiesProvider includeSubAccountPositions>
      <PageLayout className="max-w-6xl gap-6 px-3">
        <PageHero
          title={title}
          subtitle={subtitle}
          journey={['Select market', 'Open details', 'Deposit', '💰 Earn']}
          statsSlot={
            <Suspense
              fallback={
                <PageHeroStats
                  stats={[
                    { label: 'Supplied', value: '' },
                    { label: 'Borrowed', value: '' },
                    { label: 'Available', value: '' },
                  ]}
                  loading
                />
              }
            >
              <OverviewStatsContent />
            </Suspense>
          }
          pillNav={
            <PillNav
              items={[
                {
                  label: 'Markets',
                  href: basePath,
                  icon: <Boxes className="h-4 w-4" />,
                  active: viewMode === 'markets',
                },
                {
                  label: 'Assets',
                  href: assetsPath,
                  icon: <Rows3 className="h-4 w-4" />,
                  active: viewMode === 'assets',
                },
                {
                  label: 'Vaults',
                  href: vaultsPath,
                  icon: <Shield className="h-4 w-4" />,
                },
              ]}
            />
          }
        />

        {viewMode === 'markets' ? (
          <MarketsOverviewSection />
        ) : (
          <>
            <FeaturedOpportunities />
            <UnifiedEarnTableSection basePath={assetsPath} />
          </>
        )}
      </PageLayout>
    </UnifiedEarnOpportunitiesProvider>
  )
}
